import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_INTERCOM } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import SelectableCard from "@/design/components/SelectableCard";
import FormGroup from "@/design/forms/FormGroup";
import InputMedia from "@/design/forms/InputMedia";
import InputNumber from "@/design/forms/InputNumber";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viGear, viMegaphone, viPager, viPerson } from "@sippy-platform/valkyrie";

import { Box, Container, Grid, IconButton, List, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import intercomReducer from "./intercomReducer";
import IntercomMembers from "./members/IntercomMembers";
import MemberItem from "./members/MemberItem";

function Intercom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const soundStore = useSelector((state) => state.domain.sounds);

  const handleTab = (event, newTab) => setTab(newTab);

  const [tab, setTab] = useState(0);

  // Get general data
  useEffect(() => {
    Promise.all([Api.getSounds(), Api.getUsers()]);
  }, []);

  const {
    value: intercom,
    dispatch: intercomDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_INTERCOM,
    reducer: intercomReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      maxMembers: number()
        .required({
          condition: intercom.type === "RingallGroup",
          message: "Ring seconds is required.",
        })
        .min(2, { message: "Number of members must be between 2 and 15.." })
        .max(15, { message: "Number of members must be between 2 and 15.." }),
    }),
    onSubmit: () => Api.storeIntercom(intercom),
    onSuccess: (response) => {
      if (!intercom.id) {
        intercomDispatch({ type: "id", value: response.data.intercom.id });
        navigate(`../${response.data.intercom.id}`);
      }

      Promise.resolve(Api.getIntercoms());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === intercom.id || !id) {
      return;
    }

    setTab(0);

    if (id === "new") {
      Api.cancelActiveGetRequests();
      intercomDispatch({ type: "set", value: NEW_INTERCOM });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getIntercom({ id: Number(id) }))
      .then((response) => !ignore && intercomDispatch({ type: "set", value: response.data.intercom }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [id, intercom.id, intercomDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    intercomDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getIntercoms()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${intercom.name || "Intercom"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "Settings", icon: viGear, id: "settings" },
            {
              label: "Members",
              icon: viPerson,
              id: "members",
              disabled: !intercom.id,
            },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={intercom.name} icon={viMegaphone} />
              </IconButton>
            ) : (
              <Avatar text={intercom.name} icon={viMegaphone} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={intercom.id || "nk"}
                isActionable={!!intercom?.id}
                onDeleteCheck={() => Api.deleteIntercom(intercom, false)}
                onDelete={() => Api.deleteIntercom(intercom, true)}
                name={`${intercom.name} (${intercom.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={intercom.name || "Unnamed intercom"}
          subheader={intercom.exten || "No number set"}
        />
        {tab === 0 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="name"
                  label="Name"
                  onChange={formHandler}
                  value={intercom.name}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumber
                  required
                  id="exten"
                  label="Number"
                  onChange={formHandler}
                  value={intercom.exten}
                  error={errors}
                  helperText="Number cannot be 0 or any Belgian emergency number."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Intercom mode">
              <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
                <SelectableCard
                  selected={!intercom.fullDuplexAudio}
                  onClick={() => formHandler("fullDuplexAudio", false)}
                  icon={viPager}
                  title="Pager"
                  description="Allow only the caller to speak to all members of the intercom."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
                <SelectableCard
                  selected={intercom.fullDuplexAudio}
                  onClick={() => formHandler("fullDuplexAudio", true)}
                  icon={viMegaphone}
                  title="Intercom"
                  description="Allow two-way communication between the participants of the intercom call."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Settings">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="dialInUse"
                  onChange={formHandler}
                  states={[false, true]}
                  value={intercom.dialInUse}
                  primary="Dial members in ongoing calls"
                  secondary="Calls will show up as new calls and auto-answer won't work."
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="playBeep"
                  onChange={formHandler}
                  states={[false, true]}
                  value={intercom.playBeep}
                  primary="Play a beep to the caller when starting"
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Announcement">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputMedia
                  hasEmpty={null}
                  id="announcementId"
                  label="Announcement"
                  onChange={formHandler}
                  value={intercom.announcementId}
                  selectValue="id"
                  selects={soundStore.list}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                  getSourceDependency={(option) => option?.token}
                  getSourceGeneration={(option) =>
                    `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
                  }
                  helperText="The announcement will be played to all participants."
                />
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 1 && (
          <FormGroup
            title={`Members \u00b7 ${intercom.members.length}/15`}
            actions={<IntercomMembers intercom={intercom} intercomDispatch={intercomDispatch} />}
          >
            <Grid size={12}>
              {intercom.members.length >= 1 ? (
                <>
                  <List>
                    {intercom.members.map((member) => (
                      <MemberItem
                        key={member.exten}
                        member={member}
                        intercom={intercom}
                        intercomDispatch={intercomDispatch}
                      />
                    ))}
                  </List>
                </>
              ) : (
                <Box
                  sx={[
                    {
                      borderRadius: 1,
                      gridColumn: "1 / -1",
                      alignItems: "center",
                      px: 1.75,
                      py: 1.5,
                      bgcolor: (theme) => (theme.palette.mode === "light" ? "#f8f8f8" : "#28282d"),
                    },
                  ]}
                >
                  <Typography variant="body2">This intercom has no members...</Typography>
                </Box>
              )}
            </Grid>
          </FormGroup>
        )}
      </Stack>
    </Container>
  );
}

export default Intercom;
